import { get } from "../client";
import {
  PublicProfileApiResponse,
  TransformedProfileData,
} from "../../types/publicProfile";
import { getFullMediaUrl, getProfileImageUrl } from "../../utils/media";
import endpoints from "../endpoints";

export const getPublicProfile = async (
  uuid: string,
  isPreview: boolean = false
) => {
  const endpoint = isPreview
    ? `${endpoints.public.getOrganizationPreview}${uuid}`
    : `${endpoints.public.getOrganizationInfo}${uuid}`;

  const response = await get<PublicProfileApiResponse>(endpoint);

  if (!response.ok) {
    throw new Error(`Failed to fetch profile: ${response.statusText}`);
  }

  return response.data;
};

// Helper function to determine card privacy settings
export const getCardPrivacy = (
  profileData: PublicProfileApiResponse,
  cardId?: string
) => {
  const cardInfo = profileData.card_info;
  const organizationSettings = profileData.profile_json.organizationSettings;

  // If cardId is provided, find specific card settings
  if (cardId) {
    const cardSettings = organizationSettings.cards.find(
      (card) => card.cardId === cardId
    );
    if (cardSettings) {
      return cardSettings.sectionPrivacy;
    }
  }

  // Use card_info privacy settings as default
  return cardInfo.privacy.sectionPrivacy;
};

// Transform API response to match the existing portfolio component structure
export const transformProfileData = (
  apiResponse: PublicProfileApiResponse,
  cardId?: string
): TransformedProfileData => {
  const {
    profile_json,
    card_info,
    uuid,
    organization_name,
    qr_image,
    card_type,
    user_info,
  } = apiResponse;
  const { sharedContent } = profile_json;
  const cardPrivacy = getCardPrivacy(apiResponse, cardId);

  return {
    id: apiResponse?.id,
    personalInfo: {
      name:
        sharedContent.personalInfo.name ||
        `${user_info?.first_name} ${user_info?.last_name}`.trim(),
      headline:
        sharedContent.personalInfo.headline || apiResponse.job_title || "",
      avatar: getProfileImageUrl(
        sharedContent.personalInfo.avatar || user_info?.image,
        sharedContent.personalInfo.name ||
          `${user_info?.first_name} ${user_info?.last_name}`.trim()
      ),
      location:
        sharedContent.personalInfo.location ||
        [apiResponse.city, apiResponse.state, apiResponse.country]
          .filter(Boolean)
          .join(", ") ||
        "",
      profilePic: user_info?.image || "",
      email: sharedContent.personalInfo.email || apiResponse.email,
      phone: sharedContent.personalInfo.phone || apiResponse.phone || "",
      website: sharedContent.personalInfo.website || apiResponse.website || "",
      bio: sharedContent.personalInfo.bio || "",
      languages: sharedContent.personalInfo.languages || [],
      timezone: sharedContent.personalInfo.timezone || "",
      availableForWork: sharedContent.personalInfo.availableForWork || false,
      preferredContactMethod:
        sharedContent.personalInfo.preferredContactMethod || "email",
      calendlyUrl: sharedContent.personalInfo.calendlyUrl || "",
    },
    sections: {
      about: {
        content: sharedContent.sections.about.content || "",
        privacy: { isVisible: cardPrivacy.about?.isVisible ?? true },
      },
      experience: {
        items:
          sharedContent.sections.experience.items.filter(
            (item) => item.privacy?.isVisible !== false
          ) || [],
        privacy: { isVisible: cardPrivacy.experience?.isVisible ?? true },
      },
      education: {
        items:
          sharedContent.sections.education.items.filter(
            (item) => item.privacy?.isVisible !== false
          ) || [],
        privacy: { isVisible: cardPrivacy.education?.isVisible ?? true },
      },
      skills: {
        items:
          sharedContent.sections.skills.items.filter(
            (item) => item.privacy?.isVisible !== false
          ) || [],
        privacy: { isVisible: cardPrivacy.skills?.isVisible ?? true },
      },
      reels: {
        items:
          sharedContent.sections.reels.items.map((reel) => ({
            ...reel,
            videoUrl: getFullMediaUrl(reel.videoUrl),
          })) || [],
        privacy: { isVisible: cardPrivacy.reels?.isVisible ?? true },
      },
      customLinks: {
        items:
          sharedContent.sections.customLinks.items.filter(
            (item) => item.privacy?.isVisible !== false
          ) || [],
        privacy: { isVisible: cardPrivacy.customLinks?.isVisible ?? true },
      },
      socialMedia: {
        items:
          sharedContent.sections.socialMedia.items.filter(
            (item) => item.privacy?.isVisible !== false
          ) || [],
        privacy: { isVisible: cardPrivacy.socialMedia?.isVisible ?? true },
      },
      businessCard: {
        data: sharedContent.sections.businessCard.data,
        privacy: { isVisible: cardPrivacy.businessCard?.isVisible ?? true },
      },
      // Default empty sections with privacy settings
      socials: {
        items: [],
        privacy: { isVisible: cardPrivacy.socials?.isVisible ?? true },
      },
      stats: {
        items: sharedContent.sections.stats.items || [],
        privacy: { isVisible: cardPrivacy.stats?.isVisible ?? true },
      },
      certifications: {
        items: sharedContent.sections.certifications.items || [],
        privacy: { isVisible: cardPrivacy.certifications?.isVisible ?? true },
      },
      awards: {
        items: sharedContent.sections.awards.items || [],
        privacy: { isVisible: cardPrivacy.awards?.isVisible ?? true },
      },
      projects: {
        items: sharedContent.sections.projects.items || [],
        privacy: { isVisible: cardPrivacy.projects?.isVisible ?? true },
      },
      services: {
        items: sharedContent.sections.services.items || [],
        privacy: { isVisible: cardPrivacy.services?.isVisible ?? true },
      },
      testimonials: {
        items: sharedContent.sections.testimonials.items || [],
        privacy: { isVisible: cardPrivacy.testimonials?.isVisible ?? true },
      },
      blog: {
        items: sharedContent.sections.blog.items || [],
        privacy: { isVisible: cardPrivacy.blog?.isVisible ?? true },
      },
      volunteer: {
        items: sharedContent.sections.volunteer.items || [],
        privacy: { isVisible: cardPrivacy.volunteer?.isVisible ?? true },
      },
      publications: {
        items: sharedContent.sections.publications.items || [],
        privacy: { isVisible: cardPrivacy.publications?.isVisible ?? true },
      },
    },
    contactInfo: {
      email: sharedContent.personalInfo.email || apiResponse.email,
      phone: sharedContent.personalInfo.phone || apiResponse.phone || "",
      website: sharedContent.personalInfo.website || apiResponse.website || "",
      location:
        sharedContent.personalInfo.location ||
        [apiResponse.city, apiResponse.state, apiResponse.country]
          .filter(Boolean)
          .join(", ") ||
        "",
      timezone: sharedContent.personalInfo.timezone || "",
      availableForWork: sharedContent.personalInfo.availableForWork || false,
      preferredContactMethod:
        sharedContent.personalInfo.preferredContactMethod || "email",
      calendlyUrl: sharedContent.personalInfo.calendlyUrl || "",
    },
    metadata: {
      version: sharedContent.metadata.version || "1.0.0",
      lastUpdated: sharedContent.metadata.lastUpdated || apiResponse.updated_at,
      theme: sharedContent.metadata.theme || "modern",
      seoSettings: {
        title:
          sharedContent.metadata.seoSettings?.title ||
          `${sharedContent.personalInfo.name} - ${sharedContent.personalInfo.headline}`,
        description: sharedContent.metadata.seoSettings?.description || "",
        keywords: sharedContent.metadata.seoSettings?.keywords || [],
      },
    },
    cardInfo: card_info,
    organizationInfo: {
      uuid,
      name: organization_name,
      qr_image,
      card_type,
    },
    cardImages: {
      front: getFullMediaUrl(card_info.front_side_image),
      back: getFullMediaUrl(card_info.back_side_image),
    },
  };
};
